import { useState, useEffect } from 'react';
import { Link as ReactLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useAuth } from '@praxis/component-auth';
import { TrackPageView } from '../../../utils/analyticsTracking';
import { useInstallerAuthContext } from '../../../hooks/useInstallerAuthContext';
import {
  handleSelectChange,
  displayNamesConverter,
  createHostname,
  createFQDN,
} from '../../../utils/onboardingHelpers';
import { PhotoUploader } from '../../../components/PhotoUploader';
import {
  addNewReader,
  addNewReaderExt,
  selectMacForDevices,
} from '../../../app/onboarding/onboardingSlice';
import {
  getReaderNames,
  getReaderNamesExt,
  selectDisplayNames,
  selectMacAddresses,
  selectAddress,
  selectLocationDetailsLoading,
} from '../../../app/onboarding/locationDetailsSlice';
import { convertObjArrayToSelectOptions } from '../../../utils/helpers';
import {
  Card,
  Grid,
  Breadcrumbs,
  Layout,
  Heading,
  Form,
  Spinner,
  ExpandableSection,
  Anchor,
} from '@enterprise-ui/canvas-ui-react';
import EnterpriseIcon, {
  ErrorFilledIcon,
  TrashIcon,
} from '@enterprise-ui/icons';
import { Button } from 'ui-library';
import { Message } from '../../../components/Message';
import { SelectInput } from 'ui-library';
import { CreatableSelect } from '../../../components/CreatableSelect';

import styles from './OnboardingForm.module.scss';
import { HeightFormField } from '../../../components/HeightFormField';

export const OnboardingForm = () => {
  /* Auth related constants */
  const auth = useAuth();
  const { session } = auth;
  const { user } = useInstallerAuthContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  TrackPageView('/onboarding', 'onboardingForm');

  const { loading, error } = useSelector((state) => state.onboarding);
  const registeredReaders = useSelector(selectMacForDevices);
  const displayNames = displayNamesConverter(useSelector(selectDisplayNames));

  const macAddresses = convertObjArrayToSelectOptions(
    useSelector(selectMacAddresses),
  );
  const storeAddress = useSelector(selectAddress);
  const locationDetailsIsLoading = useSelector(selectLocationDetailsLoading);

  const {
    // Creates form handlers and errors
    register,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      locationId: 'T',
    },
    mode: 'onBlur',
  });

  const [inputData, setInputData] = useState({
    logicalName: '',
    macAddress: '',
    hostName: '',
    locationId: '',
    image: null,
    fqdn: '',
    deviceType: '',
  });
  const [addReaderStatus, setAddReaderStatus] = useState('idle');
  const [location, setLocation] = useState('T');
  const [installLocation, setInstallLocation] = useState('T');
  const [token, setToken] = useState('');
  const [invalidMac, setInvalidMac] = useState(false);
  const [macAddressError, setMacAddressError] = useState('');
  let activeLocationId =
    (location?.length > 1 || installLocation?.length > 1) && storeAddress; //displays store address if on or off vpn
  const [isPhotoMinHit, setIsPhotoMinHit] = useState(false);
  let [isPhotoMaxHit, setIsPhotoMaxHit] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUpdateValid, setIsUpdateValid] = useState(false);
  const atrFields =
    inputData.deviceType === 'ATR7000'
      ? [
          inputData.readerHeightInput,
          inputData.leftWidthInput,
          inputData.rightWidthInput,
          inputData.distanceToDoorInput,
          isUpdateValid,
        ]
      : [];
  useEffect(() => {
    // Run prefill form onLoad when macAddress is scanned or if installer user exists
    if (user) {
      let { installLocation, token } = user;
      setInstallLocation(installLocation);
      setToken(token);
      setValue('locationId', installLocation);

      setInputData((prevData) => ({
        ...prevData,
        locationId: installLocation,
      }));
    }
  }, [user, setValue]);

  useEffect(() => {
    if (location.length > 4) {
      //fix bug where location wasn't updating depending on user/session and causing reader names to not populate on load
      if (session) {
        dispatch(getReaderNames(location));
      } else {
        dispatch(getReaderNamesExt({ installLocation: location, token }));
      }
      // If praxis session, dispatch action with automatic HTTP headers
    } else if (installLocation.length > 4) {
      // Otherwise, dispatch action with generated bearer token
      dispatch(getReaderNamesExt({ installLocation, token }));
    }
  }, [dispatch, location, installLocation, token, session]);

  useEffect(() => {
    // Creates hostname when macAddress is entered
    if (inputData.macAddress.length > 11 && inputData.deviceType) {
      createHostname(inputData.macAddress, inputData.deviceType).then(
        (host) => {
          setInputData((prevData) => ({
            ...prevData,
            hostName: host,
          }));
          // setHostname(host);
        },
      );
    }
    // Sets domain after hostname is created
    if (inputData.hostName !== '') {
      let domain = createFQDN(inputData.hostName, inputData.locationId);
      setInputData((prevData) => ({
        ...prevData,
        fqdn: domain,
      }));
    }
  }, [
    inputData.macAddress,
    inputData.hostName,
    inputData.locationId,
    inputData.deviceType,
  ]);

  useEffect(() => {
    if (!inputData.image) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(inputData.image);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [inputData.image, setPreviewUrl]);

  useEffect(() => {
    // Stores successfully registered readers in local storage
    localStorage.setItem('rfidReaders', JSON.stringify(registeredReaders));
  }, [registeredReaders]);
  const canSubmit =
    [
      ...atrFields,
      inputData.logicalName,
      inputData.macAddress,
      inputData.hostName,

      inputData.locationId,
      inputData.image,
      inputData.deviceType,
    ].every(Boolean) &&
    addReaderStatus === 'idle' &&
    !invalidMac;
  // Gathers all metadata and sends it to API
  const submitForm = async () => {
    if (canSubmit) {
      try {
        setAddReaderStatus('pending');

        if (session) {
          await dispatch(addNewReader(inputData)).unwrap();
        } else if (user) {
          await dispatch(addNewReaderExt({ inputData, token })).unwrap();
        }

        for (let key in inputData) {
          if (typeof inputData[key] === 'string') {
            setInputData({ ...inputData, [key]: '' });
          }
        }

        navigate('/success', { replace: true });
      } catch (err) {
        console.log('Failed to onboard device!', err);
      } finally {
        setAddReaderStatus('idle');
      }
    }
  };

  return (
    <>
      <Layout.Body data-testid="onboardingForm" includeRail>
        <Card className={`hc-pa-normal ${styles.cardContainer}`}>
          <Grid.Container xs={12}>
            <Grid.Item xs={12}>
              <Breadcrumbs>
                <Breadcrumbs.Item as={ReactLink} to="/">
                  Home
                </Breadcrumbs.Item>
                <Breadcrumbs.Item as={ReactLink} to="/onboarding">
                  Onboarding
                </Breadcrumbs.Item>
              </Breadcrumbs>
            </Grid.Item>
            <Grid.Item xs={12}>
              <Heading size={3} className="hc-mb-md">
                Device Onboarding
              </Heading>
            </Grid.Item>
          </Grid.Container>
          <Form
            role="form"
            id="registration_form"
            className="hc-mt-md"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm(e);
            }}
          >
            <div className={styles.locationFieldWrapper}>
              <Form.Field
                id="locationId"
                data-testid="locationId"
                label="Location ID"
                type="text"
                required={true}
                className="hc-mb-md"
                error={errors.locationId}
                errorText={errors.locationId && errors.locationId.message}
                {...register('locationId', {
                  required: true,
                  pattern: {
                    value: /T[0-9]{4}/,
                    message:
                      'Invalid location ID! Please follow this format: T1234',
                  },
                  onBlur: (e) => {
                    setLocation(e.target.value);
                    setInputData({ ...inputData, locationId: e.target.value });
                  },
                })}
              />
              {locationDetailsIsLoading && (
                <Spinner className={styles.inlineTinySpinner} />
              )}
              {!errors.locationId && activeLocationId && (
                <p className={`hc-fs-xs ${styles.address}`}>
                  <strong>Address:</strong> <em>{storeAddress}</em>
                </p>
              )}
            </div>
            <SelectInput
              id="displayName"
              label="Display Name"
              name="displayName"
              data-testid="display_name"
              placeholder="Select..."
              options={displayNames}
              required
              onChange={(e) => {
                let index = e.value.indexOf('-') - 1;
                let logicalName = e.value.substring(0, index);
                setInputData({
                  ...inputData,
                  logicalName: logicalName,
                  deviceType: e.device,
                });
              }}
              className="hc-mb-xl"
              aria-labelledby={`displayNameLabel`}
              classNamePrefix="customSelect"
            />
            {inputData.logicalName && (
              <p className={`hc-fs-xs ${styles.address}`}>
                <strong>Device Type:</strong> <em> {inputData.deviceType}</em>
              </p>
            )}
            <CreatableSelect
              id="macAddress"
              label="MAC Address**"
              name="macAddress"
              options={macAddresses}
              required
              onChange={(value, actionType) => {
                handleSelectChange(
                  value,
                  actionType,
                  invalidMac,
                  setInvalidMac,
                  inputData,
                  setInputData,
                  setMacAddressError,
                );
              }}
              // onBlur={validateMac}
              createLabel="Add new MAC address..."
              customPlaceholder="Select or enter new MAC address"
              className={invalidMac ? 'invalid-form-field' : ''}
            />
            {invalidMac ? (
              <p className="C-Input--type-info isErrored xs-neg-top-margin">
                <EnterpriseIcon
                  icon={ErrorFilledIcon}
                  size="inline"
                  className="hc-mr-min"
                />
                {macAddressError}
              </p>
            ) : (
              <p className="C-Input--type-info hc-mb-lg">
                <strong>
                  **Please type in your MAC address if not currently listed
                </strong>
              </p>
            )}
            {inputData.deviceType === 'ATR7000' && (
              <>
                {' '}
                <HeightFormField
                  isChangeValid={setIsUpdateValid}
                  label={'Reader Height (Floor to Bottom of Device)'}
                  onChange={(e) =>
                    setInputData({ ...inputData, readerHeightInput: e })
                  }
                />
                <HeightFormField
                  isChangeValid={setIsUpdateValid}
                  label={
                    'Distance from the center of the reader to the left border'
                  }
                  onChange={(e) =>
                    setInputData({ ...inputData, leftWidthInput: e })
                  }
                />
                <ExpandableSection className="hc-bg-grey06 hc-mv-lg">
                  <h5>Need help?</h5>
                  <ExpandableSection.Content className="hc-pa-none">
                    <img
                      width="100%"
                      height="90%"
                      src={'images/ATR-onboarding-height-width.png'}
                      alt="first_image"
                    />{' '}
                    <p>
                      <strong>Note:</strong> Please refer to the PDF
                      <Anchor
                        href="/docs/ATR7000_HangingReaderInstall.pdf"
                        target="_blank"
                        className="iot-link"
                      >
                        {' '}
                        Installation Manual
                      </Anchor>{' '}
                      for detailed information on what qualifies as a border. As
                      shown in the typical installation diagram above, borders
                      may consist of EAS towers or walls.
                    </p>
                  </ExpandableSection.Content>
                </ExpandableSection>
                <HeightFormField
                  isChangeValid={setIsUpdateValid}
                  label={
                    'Distance from the center of the reader to the right border'
                  }
                  onChange={(e) =>
                    setInputData({ ...inputData, rightWidthInput: e })
                  }
                />
                <HeightFormField
                  isChangeValid={setIsUpdateValid}
                  label={'Distance to Nearest Door, Elevator, or Escalator'}
                  onChange={(e) =>
                    setInputData({ ...inputData, distanceToDoorInput: e })
                  }
                />
                <ExpandableSection className="hc-bg-grey06 hc-mv-lg">
                  <h5>Need help?</h5>
                  <ExpandableSection.Content className="hc-pa-none">
                    <img
                      width="100%"
                      height="90%"
                      src={'images/ATR-onboarding-door-distance.png'}
                      alt="first_image"
                    />
                    <p>
                      <strong>Note:</strong> Please refer to the PDF
                      <Anchor
                        href="/docs/ATR7000_HangingReaderInstall.pdf"
                        target="_blank"
                        className="iot-link"
                      >
                        {' '}
                        Installation Manual
                      </Anchor>{' '}
                      for more details regarding spots like escalator and
                      elevator, as well as the installation hardware details.
                    </p>
                  </ExpandableSection.Content>
                </ExpandableSection>
              </>
            )}
            {previewUrl && (
              <div className={styles.imagePreviewWrapper}>
                <Button
                  className={styles.iconOverlay}
                  icon={TrashIcon}
                  name="icon"
                  label={''}
                  onClick={() => setInputData({ ...inputData, image: null })}
                />
                {
                  <img
                    src={previewUrl}
                    alt="preview"
                    className={styles.imagePreview}
                  />
                }
              </div>
            )}
            <PhotoUploader
              files={inputData.image ? [inputData.image] : []}
              onChange={(files) =>
                setInputData((prev) => ({
                  ...prev,
                  image: files?.length ? files[0] : null,
                }))
              }
              maximum={1}
              minimum={1}
              fileSizeLimit={8} //MB
              isMaximumHit={isPhotoMaxHit}
              isMinimumHit={isPhotoMinHit}
              setMaxLimit={setIsPhotoMaxHit}
              setMinLimit={setIsPhotoMinHit}
              deviceType={inputData.deviceType === 'ATR7000'}
            />
            {loading && <Spinner className="hc8-pa-2x" />}
            <Button
              className="hc-mt-normal"
              label="Submit"
              type="submit"
              name="submit"
              id="submit"
              fullWidth
              disabled={!canSubmit}
            />
          </Form>
          {error && (
            <Message type="error">
              <h3 className="hc-mb-xs">Something went wrong...</h3>
              <p className="hc-mb-xs">
                Failed to submit the onboarding form. Please try resubmitting or
                restarting your session.
              </p>
              <p className="hc-ta-center">
                <em>{error}</em>
              </p>
            </Message>
          )}
        </Card>
      </Layout.Body>
    </>
  );
};
